import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ship } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/config";
import { inputCls, Field, GoldButton, meetsMinimums, Honeypot, PrivacyNote } from "./fields";
import { errorMessage } from "./useMember";

const EMPTY = { name: "", phone: "", dates: "", guests: "", message: "" };

/** Charter lead for a yacht on the show. Lead is emailed to the Captain's Cut team; no payment is taken here. */
export const InquiryModal = ({ yacht, onClose, headers }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [website, setWebsite] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!meetsMinimums([[form.name, 2], [form.dates, 2]]) || !(Number(form.guests) >= 1)) {
      setShowErrors(true); toast.error("Please complete the highlighted fields."); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/applicants/${yacht.id}/inquiry`, { ...form, website, guests: Number(form.guests) }, { headers });
      toast.success(`Charter request sent for ${yacht.name}. The Captain's Cut team will be in touch.`);
      setForm(EMPTY);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Could not send your request — try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!yacht} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-[#0B111B] border border-[#D4AF37]/30 text-[#F7F8F9] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inquiry-modal">
        {yacht && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display tracking-[0.1em] text-[#F5F3EF]" data-testid="inquiry-modal-title">
                REQUEST TO CHARTER — {yacht.name}
              </DialogTitle>
            </DialogHeader>
            <p className="flex items-start gap-3 text-xs text-[#94A3B8] border border-[#D4AF37]/25 bg-[#070A0F] p-3 leading-relaxed" data-testid="inquiry-note">
              <Ship size={16} className="text-[#E8C15A] shrink-0 mt-0.5" />
              <span>Captain's Cut arranges charters directly with the owner. Send your request and our team will reply
                with availability and pricing — nothing is charged here.</span>
            </p>
            <form onSubmit={submit} noValidate className="space-y-4 mt-2">
              <Field label="Your name" value={form.name} minLength={2} showErrors={showErrors} testId="inquiry-name">
                <input required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="inquiry-name" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Phone" hint="optional">
                  <input value={form.phone} onChange={set("phone")} placeholder="+1 …" className={inputCls} data-testid="inquiry-phone" />
                </Field>
                <Field label="Guests">
                  <input required type="number" min={1} max={500} value={form.guests} onChange={set("guests")} placeholder="e.g. 12" className={inputCls} data-testid="inquiry-guests" />
                </Field>
              </div>
              <Field label="Preferred dates" value={form.dates} minLength={2} showErrors={showErrors} testId="inquiry-dates">
                <input required value={form.dates} onChange={set("dates")} placeholder="e.g. Aug 10–14, flexible" className={inputCls} data-testid="inquiry-dates" />
              </Field>
              <Field label="Tell us about the trip" hint="optional">
                <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Occasion, route, must-haves…" className={inputCls} data-testid="inquiry-message" />
              </Field>
              <Honeypot value={website} onChange={(e) => setWebsite(e.target.value)} />
              <PrivacyNote>Your request is shared only with the Captain's Cut team to arrange the charter.</PrivacyNote>
              <GoldButton type="submit" disabled={busy} data-testid="inquiry-submit">{busy ? "SENDING…" : "SEND CHARTER REQUEST"}</GoldButton>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
